import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { FormattedValue } from "./StatTile";
import { formatPercent, share } from "../lib/columns";

export interface RankedItem {
	key: string | number;
	label: string;
	value: number;
	/** Secondary text next to the label (e.g. "12 invoices"). */
	sub?: string;
	href?: string;
}

interface RankedBarsProps {
	title: string;
	items: RankedItem[];
	format?: "money" | "number";
	currency?: string;
	/** Rows shown; the rest are folded into one "Other" row so the total stays whole. */
	limit?: number;
	/** Show each row's share of the total next to its value. */
	showShare?: boolean;
	className?: string;
}

/**
 * Ranked horizontal bars: label and value are printed on every row, so the bar is only a reading aid and the
 * list stays readable without color (and in print). One hue, one scale, largest first.
 */
export function RankedBars({
	title,
	items,
	format = "money",
	currency,
	limit = 8,
	showShare = false,
	className,
}: RankedBarsProps) {
	const { t } = useTranslation();
	const positive = items.filter(i => i.value > 0).sort((a, b) => b.value - a.value);
	const total = positive.reduce((sum, i) => sum + i.value, 0);
	const head = positive.slice(0, limit);
	const rest = positive.slice(limit);
	const rows: RankedItem[] =
		rest.length > 0
			? [
					...head,
					{
						key: "__other",
						label: t("reports.chart.other", { count: rest.length }),
						value: rest.reduce((sum, i) => sum + i.value, 0),
					},
				]
			: head;
	const max = rows.reduce((m, i) => Math.max(m, i.value), 0);

	return (
		<section className={cn("min-w-0 rounded-2xl border bg-card p-4 shadow-sm", className)}>
			<h3 className="mb-3 text-sm font-semibold">{title}</h3>
			{rows.length === 0 ? (
				<p className="py-6 text-center text-sm text-muted-foreground">{t("reports.chart.nothingToChart")}</p>
			) : (
				<ol className="space-y-3">
					{rows.map(item => (
						<li key={item.key}>
							<div className="flex items-baseline justify-between gap-3 text-sm">
								<span className="min-w-0 truncate font-medium" title={item.label}>
									{item.href ? (
										<Link to={item.href} className="hover:underline">
											{item.label}
										</Link>
									) : (
										item.label
									)}
									{item.sub && (
										<span className="ms-2 text-xs font-normal text-muted-foreground">
											{item.sub}
										</span>
									)}
								</span>
								<span className="shrink-0 tabular-nums">
									<FormattedValue value={item.value} format={format} currency={currency} />
									{showShare && (
										<span className="ms-2 text-xs text-muted-foreground" dir="ltr">
											{formatPercent(share(item.value, total))}
										</span>
									)}
								</span>
							</div>
							<div className="mt-1.5 h-2 rounded-full bg-muted print:border" aria-hidden="true">
								<div
									className={cn(
										"h-2 rounded-full",
										item.key === "__other" ? "bg-muted-foreground/40" : "bg-primary",
									)}
									style={{ width: `${max ? Math.max(2, (item.value / max) * 100) : 0}%` }}
								/>
							</div>
						</li>
					))}
				</ol>
			)}
		</section>
	);
}
